import { useMemo } from 'react';
import { type CommandItem, type KeyBinding } from 'agentable-canvas/general';
import { focusPanelInCanvas, openPanelInCanvas } from 'agentable-canvas/whiteboard';
import { openOnboardingChatPanel, openProjectBriefPanel } from './useOnboardingPanelsBridge';
import { dispatchOpenSiteOnCanvas } from './useSitePanelsBridge';
import { useAuth } from '../contexts/AuthContext';
import { useCanvasSession } from '../contexts/CanvasSessionContext';
import { useUiOverlay } from '../contexts/useUiOverlay';
import {
  dispatchChatCloseTab,
  dispatchChatFocusInput,
  dispatchChatNewTab,
  dispatchChatNextTab,
  dispatchChatPrevTab,
} from '../lib/chat-panel-events';
import {
  dispatchOpenFileManager,
  openFileManagerPanel,
  toggleLayersPanel,
  toggleSiteActionsTool,
} from '../lib/canvas-tool-actions';
import { getRecentSites, type RecentSite } from '../lib/recent-sites';
import type { SiteListItem } from '../lib/sites-api';

export type CanvasQuickNavAction =
  | 'open-in-canvas'
  | 'preview-edit'
  | 'settings'
  | 'chat'
  | 'seo'
  | 'collections'
  | 'publish';

function dispatchQuickNav(action: CanvasQuickNavAction, siteId: string | null): void {
  window.dispatchEvent(
    new CustomEvent('landi:canvas-quicknav', {
      detail: { action, siteId, timestamp: new Date().toISOString() },
      bubbles: true,
      composed: true,
    }),
  );
}

export interface StudioCommandsContext {
  siteId: string | null;
  hasSite: boolean;
  homeVisible: boolean;
  sites: SiteListItem[];
  recentSites: RecentSite[];
  showHome: () => void;
  hideHome: () => void;
  toggleSidebar: () => void;
  openSidebar: () => void;
  loadSite: (siteId: string, siteMeta?: SiteListItem) => boolean;
  publishSite: () => Promise<void>;
  openLogin: (reason?: string) => void;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  isAuthConfigured: boolean;
}

export interface UseStudioCommandsOptions {
  setPaletteOpen: (open: boolean) => void;
  sites?: SiteListItem[];
}

export interface UseStudioCommandsResult {
  commands: CommandItem[];
  bindings: KeyBinding[];
}

function openPanel(panelId: string, title: string, hideHome: () => void): void {
  hideHome();
  openPanelInCanvas(panelId, {
    focus: true,
    assignToSiteGroup: panelId === 'all-sites' ? false : undefined,
    panelProps: { __title: title },
  });
}

/** Build the studio command registry — mirrors landi-orchestration-ui categories. */
export function buildStudioCommands(ctx: StudioCommandsContext): CommandItem[] {
  const {
    siteId,
    hasSite,
    showHome,
    hideHome,
    toggleSidebar,
    openSidebar,
    loadSite,
    publishSite,
    openLogin,
    signOut,
    isAuthenticated,
    isAuthConfigured,
    sites,
    recentSites,
  } = ctx;

  const goNewSite = (): void => {
    showHome();
    openSidebar();
  };

  const openSiteById = (id: string, meta?: SiteListItem): void => {
    const loaded = loadSite(id, meta);
    if (!loaded) return;
    hideHome();
    dispatchOpenSiteOnCanvas({ siteId: id });
  };

  const siteCommands: CommandItem[] = [];

  for (const site of sites) {
    siteCommands.push({
      id: `site.open.${site.id}`,
      group: 'Sites',
      label: `Open site: ${site.name}`,
      icon: 'doc',
      description: site.subdomain ?? site.id,
      run: () => openSiteById(site.id, site),
    });
  }

  for (const recent of recentSites) {
    if (sites.some((s) => s.id === recent.siteId)) continue;
    siteCommands.push({
      id: `site.recent.${recent.siteId}`,
      group: 'Sites',
      label: `Open site: ${recent.title}`,
      icon: 'doc',
      description: recent.siteId,
      run: () => openSiteById(recent.siteId),
    });
  }

  return [
    // ── View ──────────────────────────────────────────────────────
    {
      id: 'view.toggle-sidebar',
      group: 'View',
      label: 'Toggle navigation sidebar',
      icon: 'layers',
      keys: 'mod+b',
      run: toggleSidebar,
    },
    {
      id: 'view.toggle-layers',
      group: 'View',
      label: 'Toggle layers panel',
      icon: 'layers',
      keys: 'mod+shift+l',
      available: () => !ctx.homeVisible,
      run: toggleLayersPanel,
    },
    {
      id: 'view.toggle-publish-tools',
      group: 'View',
      label: 'Toggle publish tools',
      icon: 'sliders',
      available: () => hasSite && !ctx.homeVisible,
      run: toggleSiteActionsTool,
    },
    // ── Panels ────────────────────────────────────────────────────
    {
      id: 'panel.chat',
      group: 'Panels',
      label: 'Open chat panel',
      icon: 'chat',
      keys: 'mod+shift+c',
      run: () => {
        hideHome();
        openOnboardingChatPanel();
      },
    },
    {
      id: 'panel.brief',
      group: 'Panels',
      label: 'Open project brief panel',
      icon: 'doc',
      run: () => {
        hideHome();
        openProjectBriefPanel();
      },
    },
    {
      id: 'panel.preview',
      group: 'Panels',
      label: 'Open preview panel',
      icon: 'board',
      available: () => hasSite,
      run: () => {
        hideHome();
        openPanelInCanvas('web-preview', {
          focus: true,
          panelProps: { siteId, __title: 'Preview' },
        });
      },
    },
    {
      id: 'panel.file-manager',
      group: 'Panels',
      label: 'Open file manager',
      icon: 'artifact',
      available: () => hasSite,
      run: () => {
        hideHome();
        if (siteId) {
          openFileManagerPanel(siteId);
        } else {
          dispatchOpenFileManager();
        }
      },
    },
    {
      id: 'panel.all-sites',
      group: 'Panels',
      label: 'Open all sites',
      icon: 'grid',
      run: () => {
        hideHome();
        openPanelInCanvas('all-sites', {
          focus: true,
          assignToSiteGroup: false,
          size: { w: 520, h: 640 },
          panelProps: { __title: 'All Sites' },
        });
      },
    },
    {
      id: 'panel.upload-media',
      group: 'Panels',
      label: 'Upload media (file manager)',
      icon: 'download',
      description: 'Open file manager to browse and manage site assets',
      available: () => hasSite,
      run: () => {
        hideHome();
        dispatchOpenFileManager();
      },
    },
    // ── Navigation ────────────────────────────────────────────────
    {
      id: 'nav.new-site',
      group: 'Navigation',
      label: 'Create new site',
      icon: 'plus',
      keys: 'mod+alt+n',
      description: 'Start a new chat and site generation flow',
      run: goNewSite,
    },
    {
      id: 'nav.go-home',
      group: 'Navigation',
      label: 'Go to home prompt',
      icon: 'spark',
      run: goNewSite,
    },
    {
      id: 'nav.all-sites',
      group: 'Navigation',
      label: 'Browse all sites',
      icon: 'grid',
      run: () => openPanel('all-sites', 'All Sites', hideHome),
    },
    // ── Chat ──────────────────────────────────────────────────────
    {
      id: 'chat.new-tab',
      group: 'Chat',
      label: 'New chat tab',
      icon: 'plus',
      keys: 'mod+shift+t',
      run: () => {
        hideHome();
        openOnboardingChatPanel();
        dispatchChatNewTab();
      },
    },
    {
      id: 'chat.next-tab',
      group: 'Chat',
      label: 'Next chat tab',
      icon: 'arrowR',
      keys: 'mod+]',
      run: () => {
        hideHome();
        dispatchChatNextTab();
      },
    },
    {
      id: 'chat.prev-tab',
      group: 'Chat',
      label: 'Previous chat tab',
      icon: 'arrowR',
      keys: 'mod+[',
      run: () => {
        hideHome();
        dispatchChatPrevTab();
      },
    },
    {
      id: 'chat.close-tab',
      group: 'Chat',
      label: 'Close chat tab',
      icon: 'close',
      keys: 'mod+shift+w',
      run: dispatchChatCloseTab,
    },
    {
      id: 'chat.focus',
      group: 'Chat',
      label: 'Focus chat composer',
      icon: 'send',
      keys: 'mod+l',
      run: () => {
        hideHome();
        openOnboardingChatPanel();
        dispatchChatFocusInput();
      },
    },
    // ── Site actions ──────────────────────────────────────────────
    {
      id: 'site.open-canvas',
      group: 'Site',
      label: 'Open site on canvas',
      icon: 'board',
      available: () => hasSite,
      run: () => {
        if (!siteId) return;
        hideHome();
        dispatchOpenSiteOnCanvas({ siteId });
      },
    },
    {
      id: 'site.preview-edit',
      group: 'Site',
      label: 'Focus preview / edit',
      icon: 'code',
      keys: 'mod+shift+e',
      available: () => hasSite,
      run: () => {
        hideHome();
        dispatchQuickNav('preview-edit', siteId);
        focusPanelInCanvas('web-preview');
      },
    },
    {
      id: 'site.publish',
      group: 'Site',
      label: 'Publish site',
      icon: 'play',
      available: () => hasSite,
      run: () => {
        hideHome();
        void publishSite();
      },
    },
    {
      id: 'site.settings',
      group: 'Site',
      label: 'Open project brief (settings)',
      icon: 'sliders',
      available: () => hasSite,
      run: () => {
        hideHome();
        dispatchQuickNav('settings', siteId);
        openProjectBriefPanel();
      },
    },
    // ── Sites (dynamic) ───────────────────────────────────────────
    ...siteCommands,
    // ── Account ───────────────────────────────────────────────────
    ...(isAuthConfigured && !isAuthenticated
      ? [
          {
            id: 'account.sign-in',
            group: 'Account',
            label: 'Sign in',
            icon: 'check' as const,
            run: () => openLogin('to save your work'),
          },
        ]
      : []),
    ...(isAuthenticated
      ? [
          {
            id: 'account.sign-out',
            group: 'Account',
            label: 'Sign out',
            icon: 'close' as const,
            run: () => void signOut(),
          },
        ]
      : []),
    // ── Help ──────────────────────────────────────────────────────
    {
      id: 'help.palette',
      group: 'Help',
      label: 'Open command palette',
      icon: 'search',
      keys: 'mod+shift+p',
      run: () => {},
    },
  ];
}

/** Register keyboard bindings for the studio command palette. */
export function buildStudioBindings(
  commands: CommandItem[],
  setPaletteOpen: (open: boolean) => void,
): KeyBinding[] {
  const openPalette = (e: KeyboardEvent): void => {
    e.preventDefault();
    e.stopImmediatePropagation();
    setPaletteOpen(true);
  };

  const bindings: KeyBinding[] = [
    {
      keys: 'mod+k',
      label: 'Command palette',
      handler: openPalette,
    },
    {
      keys: 'mod+shift+p',
      label: 'Command palette',
      handler: openPalette,
    },
  ];

  const reservedPaletteKeys = new Set(['mod+k', 'mod+shift+p']);

  for (const cmd of commands) {
    if (!cmd.keys || cmd.id === 'help.palette') continue;
    const normalized = cmd.keys.trim().toLowerCase();
    if (reservedPaletteKeys.has(normalized)) continue;
    bindings.push({
      keys: cmd.keys,
      label: cmd.label,
      handler: () => cmd.run(),
    });
  }

  return bindings;
}

export function useStudioCommands({
  setPaletteOpen,
  sites = [],
}: UseStudioCommandsOptions): UseStudioCommandsResult {
  const { session, loadSite, publishSite } = useCanvasSession();
  const { homeVisible, showHome, hideHome, toggleSidebar, openSidebar } = useUiOverlay();
  const { user, isConfigured, openLogin, signOut } = useAuth();

  const siteId = session.siteId;
  const hasSite = Boolean(siteId);
  const recentSites = useMemo(() => getRecentSites(12), [session.updatedAt]);

  const ctx = useMemo<StudioCommandsContext>(
    () => ({
      siteId,
      hasSite,
      homeVisible,
      sites,
      recentSites,
      showHome,
      hideHome,
      toggleSidebar,
      openSidebar,
      loadSite,
      publishSite,
      openLogin,
      signOut,
      isAuthenticated: Boolean(user),
      isAuthConfigured: isConfigured,
    }),
    [
      siteId,
      hasSite,
      homeVisible,
      sites,
      recentSites,
      showHome,
      hideHome,
      toggleSidebar,
      openSidebar,
      loadSite,
      publishSite,
      openLogin,
      signOut,
      user,
      isConfigured,
    ],
  );

  const commands = useMemo(() => {
    const base = buildStudioCommands(ctx);
    return base.map((cmd) =>
      cmd.id === 'help.palette'
        ? { ...cmd, run: () => setPaletteOpen(true) }
        : cmd,
    );
  }, [ctx, setPaletteOpen]);

  const bindings = useMemo(
    () => buildStudioBindings(commands, setPaletteOpen),
    [commands, setPaletteOpen],
  );

  return { commands, bindings };
}
